/**
 * Native-app detection.
 *
 * Returns true when the current webview is running inside our wrapped native
 * iOS/Android app (Capacitor for Apple, TWA for Google Play). Used to hide
 * in-app payment CTAs and swap them for "open in browser" flows so we stay on
 * the right side of App Store guideline 3.1.5(a) and keep 100% of Stripe.
 *
 * Detection order:
 *   1. Capacitor injects `window.Capacitor` — most reliable for iOS wrapping.
 *   2. Google Play TWA opens the app from an android-app://... referrer.
 *   3. ?native=1 query param — for manual testing in the browser.
 *   4. User-agent flag — if we ever ship a native wrapper that sets one.
 */

export function isNativeApp() {
  if (typeof window === "undefined") return false;

  // Capacitor
  if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function") {
    try { if (window.Capacitor.isNativePlatform()) return true; } catch { /* no-op */ }
  }
  if (window.Capacitor?.getPlatform && window.Capacitor.getPlatform() !== "web") return true;

  // Google Play TWA
  if (document.referrer && document.referrer.startsWith("android-app://")) return true;

  // Manual override for testing
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("native") === "1") {
      localStorage.setItem("myhandyhub_native", "1");
      return true;
    }
    if (localStorage.getItem("myhandyhub_native") === "1") return true;
  } catch { /* no-op */ }

  // Bundled-app user-agent tag (set by our wrapper build)
  if (/MyHandyHubApp/i.test(navigator.userAgent || "")) return true;

  return false;
}

/**
 * Open a URL in the phone's OS browser (Safari/Chrome), NOT the webview.
 * Used for the trader subscribe flow: takes them out of the app to complete
 * payment on the web, then they return with an active subscription.
 */
export function openExternalBrowser(url) {
  if (typeof window === "undefined") return;
  // Capacitor Browser plugin, when available, gives a proper in-OS browser.
  if (window.Capacitor?.Plugins?.Browser?.open) {
    try {
      window.Capacitor.Plugins.Browser.open({ url });
      return;
    } catch { /* fall through */ }
  }
  // "_system" target is TWA / Cordova convention for OS browser
  window.open(url, "_system") || window.open(url, "_blank");
}
